import moment from 'moment'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { displayWithCurrency } from '../../../../../utility/currency'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { COMMON_ERRORS } from '../../../../../misc/Constants'
import { DATETIME_FORMAT, getMomentInCommunityTimezone } from '../../../../../utility/datesHelper'
import { requestCommunityCategoryPackageBreakdownReport } from '../../../../../actions/reportsActions'
import { deserialize } from '../../../../../utility/serialization'
import Loader from '../../../../common/loader/Loader'
import FiltersButtonDropdown, { FIXED_DATE_RANGES_FUNCTIONS } from '../../FiltersButtonDropdown'

import businessDashboardClasses from '../BusinessDashboard.module.scss'
import classes from './ProgramCategoryPackageBreakdownWidget.module.scss'
import { ReactComponent as GolferHalfIcon } from '../../../../../assets/images/common/icons/golfer-half-icon.svg'

const CONTEXT_TYPES = {
  COURSE_CATEGORY: 'course_category',
  PACKAGE_BREAKDOWN: 'package_breakdown',
}

const DEFAULT_FILTERS = {
  ...FIXED_DATE_RANGES_FUNCTIONS['thisYear'].valueFunc(),
  categoryOption: null,
  programOption: null,
}

const SingleStatistics = ({ data, label, currency }) => (
  <div className={classnames('d-flex flex-column text-center', businessDashboardClasses.statistics)}>
    <span className={businessDashboardClasses.value}>
      {currency
        ? displayWithCurrency(data?.value, currency, true)
        : data?.value
      }
    </span>
    <span className={businessDashboardClasses.topLabel}>{label}</span>
  </div>
)

SingleStatistics.propTypes = {
  data: PropTypes.shape({
    value: PropTypes.number,
  }),
  label: PropTypes.string,
  currency: PropTypes.object,
}

const BreakdownBar = ({ value, maxValue }) => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0

  return (
    <div className={classes.barBackground}>
      <div
        className={classes.barFill}
        style={{ width: `${percentage}%` }}
      />
    </div>
  )
}

BreakdownBar.propTypes = {
  value: PropTypes.number.isRequired,
  maxValue: PropTypes.number.isRequired,
}

const BreakdownItem = ({ name, nameExtra, colorDot, displayValue, percentage, value, total }) => (
  <div className={classes.breakdownItem}>
    <div className={classes.breakdownHeader}>
      <div className={classes.breakdownName}>
        {colorDot && (
          <span
            className={classes.colorDot}
            style={{ backgroundColor: colorDot }}
          />
        )}
        <span>{name}</span>
        {nameExtra && (
          <span className={classnames('ml-2', classes.packagePrice)}>
            {nameExtra}
          </span>
        )}
      </div>
      <span className={classes.breakdownValue}>
        {displayValue}
        {percentage !== undefined && (
          <span className={classes.percentageLabel}>
            ({percentage}%)
          </span>
        )}
      </span>
    </div>
    <BreakdownBar value={value} maxValue={total} />
  </div>
)

BreakdownItem.propTypes = {
  name: PropTypes.string.isRequired,
  nameExtra: PropTypes.string,
  colorDot: PropTypes.string,
  displayValue: PropTypes.node.isRequired,
  percentage: PropTypes.number,
  value: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
}

const Placeholder = () => (
  <div className={classnames('d-flex flex-column justify-content-center align-items-center text-center', businessDashboardClasses.placeholder)}>
    <GolferHalfIcon className={businessDashboardClasses.icon} />
    <h3 className={classnames('my-3', businessDashboardClasses.noActivitiesTitle)}>No Activity</h3>
    <p className={businessDashboardClasses.noActivitiesText}>
      There is no activity in the selected range. Please adjust your filters.
    </p>
  </div>
)

const ProgramCategoryPackageBreakdownWidget = () => {
  const community = useSelector(state => state.user.current_user.communities[0])
  const { communityCategories, loadingCategories } = useSelector(state => state.dashboard)

  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [activeTab, setActiveTab] = useState(CONTEXT_TYPES.COURSE_CATEGORY)
  const [showRevenue, setShowRevenue] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [data, setData] = useState()

  useEffect(() => {
    setIsLoadingData(true)

    const startDate = getMomentInCommunityTimezone(moment(filters.startDate), community?.timezone, DATETIME_FORMAT)
      .toDate()
    const endDate = getMomentInCommunityTimezone(moment(filters.endDate), community?.timezone, DATETIME_FORMAT)
      .toDate()
    const courseCategoryId = filters.categoryOption?.value
    const courseId = filters.programOption?.value

    requestCommunityCategoryPackageBreakdownReport({
      communityId: community?.id,
      startDate,
      endDate,
      contextType: activeTab,
      ...(activeTab === CONTEXT_TYPES.COURSE_CATEGORY && { withRevenueData: showRevenue }),
      courseCategoryId,
      courseId,
    })
      .then(res => {
        setData(deserialize(res.data.report.data))
        setIsLoadingData(false)
      })
      .catch(() => toast.error(COMMON_ERRORS[500]))
  }, [community, filters.startDate, filters.endDate, filters.categoryOption?.value, filters.programOption?.value, activeTab, showRevenue])

  const handleSubmitFilters = (newFilters) => {
    setFilters(prevState => ({ ...prevState, ...newFilters }))
  }

  const handleTabChange = (isRight) => {
    setActiveTab(isRight ? CONTEXT_TYPES.PACKAGE_BREAKDOWN : CONTEXT_TYPES.COURSE_CATEGORY)
    setShowRevenue(false)
  }

  const categoriesMap = useMemo(() => {
    const map = {}
    communityCategories?.forEach(c => {
      map[c.id] = c
    })
    return map
  }, [communityCategories])

  const isLoading = isLoadingData || loadingCategories

  const totalCategoryValue = data?.courseCategory?.total?.value || 0
  const totalPackagesSold = data?.packageBreakdown?.totalPackagesSold?.value || 0

  return (
    <section className={businessDashboardClasses.widget}>
      <div className={classnames('d-flex justify-content-between align-items-end', businessDashboardClasses.titleWrapper)}>
        <h2 className={businessDashboardClasses.title}>Program Category & Package Breakdown</h2>
        <FiltersButtonDropdown
          defaultFilters={DEFAULT_FILTERS}
          appliedFilters={filters}
          onSubmitFilters={handleSubmitFilters}
        />
      </div>

      <div>
        <button
          className={classnames('btn-tab', { active: activeTab === CONTEXT_TYPES.COURSE_CATEGORY })}
          onClick={() => handleTabChange(false)}
        >
          Program Category
        </button>
        <button
          className={classnames('btn-tab ml-3', { active: activeTab === CONTEXT_TYPES.PACKAGE_BREAKDOWN })}
          onClick={() => handleTabChange(true)}
        >
          Package Breakdown
        </button>
      </div>

      {isLoading ? (
        <div className='flex-grow-1 d-flex justify-content-center align-items-center'>
          <Loader>Loading data</Loader>
        </div>
      ) : (
        <>
          {activeTab === CONTEXT_TYPES.COURSE_CATEGORY ? (
            <>
              <div className={classnames('d-flex justify-content-center', businessDashboardClasses.sectionWrapper)}>
                <SingleStatistics
                  data={data?.courseCategory?.total}
                  label={showRevenue ? 'Total Est. Revenue' : 'Total Registrations'}
                  currency={showRevenue && community?.currency}
                />
              </div>

              <div>
                <button
                  className={classnames('btn-tab', { active: !showRevenue })}
                  onClick={() => setShowRevenue(false)}
                >
                  Registrations
                </button>
                <button
                  className={classnames('btn-tab ml-3', { active: showRevenue })}
                  onClick={() => setShowRevenue(true)}
                >
                  Revenue
                </button>
              </div>

              <div className={classnames('mt-2 overflow-auto', businessDashboardClasses.chartWrapper)}>
                {totalCategoryValue === 0 ? (
                  <Placeholder />
                ) : (
                  data?.courseCategory?.chartData?.map(item => {
                    const category = categoriesMap[item.id]
                    const name = category?.name || 'Other Programs'
                    const color = category?.color || '#8C9BA6'

                    return (
                      <BreakdownItem
                        key={item.id ?? 'uncategorized'}
                        name={name}
                        colorDot={color}
                        displayValue={showRevenue
                          ? displayWithCurrency(item.value, community?.currency, true)
                          : item.value
                        }
                        percentage={!showRevenue ? Math.round((item.value / totalCategoryValue) * 100) : undefined}
                        value={item.value || 0}
                        total={totalCategoryValue}
                      />
                    )
                  }))}
              </div>
            </>
          ) : (
            <>
              <div className='d-flex'>
                <div className={classnames('mr-3 flex-grow-1 d-flex justify-content-center', businessDashboardClasses.sectionWrapper)}>
                  <SingleStatistics
                    data={data?.packageBreakdown?.totalPackagesSold}
                    label='Total Packages'
                  />
                </div>
                <div className={classnames('flex-grow-1 d-flex justify-content-center', businessDashboardClasses.sectionWrapper)}>
                  <SingleStatistics
                    data={data?.packageBreakdown?.totalRevenue}
                    label='Total Est. Revenue'
                    currency={community?.currency}
                  />
                </div>
              </div>

              <div className={classnames('overflow-auto', businessDashboardClasses.chartWrapper)}>
                {totalPackagesSold === 0 ? (
                  <Placeholder />
                ) : (
                  data?.packageBreakdown?.chartData?.map(pkg => (
                    <BreakdownItem
                      key={pkg.id}
                      name={pkg.name}
                      nameExtra={displayWithCurrency(pkg.price, community?.currency, true)}
                      displayValue={pkg.count}
                      percentage={Math.round((pkg.count / totalPackagesSold) * 100)}
                      value={pkg.count || 0}
                      total={totalPackagesSold}
                    />
                  )))}
              </div>
            </>
          )}
        </>
      )}
    </section>
  )
}

export default ProgramCategoryPackageBreakdownWidget
